// react
import { Suspense, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  Link as RouterLink,
  useParams,
} from '@tanstack/react-router';

// material ui
import {
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

// api
import {
  useGetAlarmsByAccountQuery,
  useGetAlarmsFeedByAccountQuery,
} from '@/api/recalcV2/alarmApiSlice';

// shared components
import { SuperuserOnly } from '@/components/access-control';
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import LinearLoadingIndicator from '@/components/ui/progress/LinearLoadingIndicator';

// feature-components
import AlarmDataGrid from '@/features/alarm-hub/components/AlarmDataGrid';

export const Route = createFileRoute('/$accountId/_user/alarms/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('alarms');
  const { accountId } = useParams({ strict: false });

  const [useFeed, setUseFeed] = useState(true);

  const {
    data: alarmFeed,
    isLoading: isLoadingAlarmFeed,
    isFetching: isFetchingAlarmFeed,
  } = useGetAlarmsFeedByAccountQuery(accountId, {
    skip: !accountId,
    pollingInterval: 60000,
    skipPolling: !useFeed,
  });

  const {
    data: alarms,
    isLoading: isLoadingAlarms,
    isFetching: isFetchingAlarms,
  } = useGetAlarmsByAccountQuery(accountId, {
    skip: !accountId,
    pollingInterval: 60000,
    skipPolling: useFeed,
  });

  return (
    <>
      <LinearLoadingIndicator
        isLoading={
          isLoadingAlarmFeed ||
          isFetchingAlarmFeed ||
          isLoadingAlarms ||
          isFetchingAlarms
        }
      />
      <PageContainer maxWidth="xl" sx={{ py: 2 }}>
        <PageHeader title={t('overview.title')} disableArrow={true}>
          <ToggleButtonGroup
            value={useFeed ? 'feed' : 'plain'}
            exclusive
            onChange={(event, newValue) => {
              if (newValue !== null) {
                setUseFeed(newValue === 'feed');
              }
            }}
          >
            <SuperuserOnly>
              <ToggleButton value="feed">{t('toolbar.use_feed')}</ToggleButton>
              <ToggleButton value="plain">{t('toolbar.use_all')}</ToggleButton>
            </SuperuserOnly>
          </ToggleButtonGroup>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              component={RouterLink}
              to="alarm-groups"
            >
              {t('toolbar.alarm_groups')}
            </Button>
            <Button
              variant="contained"
              size="small"
              component={RouterLink}
              to="configuration"
            >
              {t('toolbar.configuration')}
            </Button>
          </Box>
        </PageHeader>

        <Stack spacing={2}>
          <Suspense>
            <AlarmDataGrid alarms={useFeed ? alarmFeed : alarms || []} />
          </Suspense>
        </Stack>
      </PageContainer>
    </>
  );
}
